import { useEffect, useState } from 'react';
import { useRouter } from '@tanstack/react-router';
import { LegacySpinner } from '@/ui/LegacySpinner';
import { useClientStore } from '@/client/client-store';
import { resolveLegacyRouteTarget } from '@/router/legacy-route-adapter';
import { openPopupMfe } from '@/mfe/popup-store';
import { useHeaderStore } from '@/header/header-store';
import { getTracker } from '@/telemetry/tracker';
import { pushToast } from '@/ui/toast-store';
import { http } from '@/http/http';
import { Endpoints } from '@/http/endpoints';
import { MotifButton, MotifTruncate } from '@ey-xd/motif-wc-react';

/**
 * My Teams panel — matching Angular's `header-myteams` component which shows
 * EY Key Contacts and Client Key Contacts fetched from the myteams API, with
 * a "See More" footer that navigates to the full My Teams MFE.
 */

interface TeamContactDto {
  id: string;
  rumId: string;
  type: 'Internal' | 'External' | 'ThirdParty';
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  team: string;
  title: string;
  keyContact: boolean;
}

interface ContactView {
  name: string;
  initials: string;
  title: string;
  email: string;
  phone: string;
}

function mapContact(c: TeamContactDto): ContactView {
  return {
    name: `${c.firstName} ${c.lastName}`.trim(),
    initials: `${c.firstName?.charAt(0) ?? ''}${c.lastName?.charAt(0) ?? ''}`.toUpperCase(),
    title: c.title ?? '',
    email: c.email ?? '',
    phone: c.phone ?? '',
  };
}

function dedupeByEmail(contacts: ContactView[]): ContactView[] {
  const seen = new Set<string>();
  return contacts.filter((c) => {
    if (!c.email || seen.has(c.email)) return false;
    seen.add(c.email);
    return true;
  });
}

function ContactCard({ contact }: { contact: ContactView }) {
  return (
    <div className="teams-panel__contact">
      <div className="teams-panel__avatar">{contact.initials}</div>
      <div className="teams-panel__details">
        <strong>
          <MotifTruncate
            maxWidth="100%"
            tooltipMaxWidth="300px"
            tooltipPlacement="left"
            tooltipStrategy="fixed"
            tooltipText={contact.name}
          >
            {contact.name}
          </MotifTruncate>
        </strong>
        {contact.title && (
          <span className="teams-panel__title">
            <MotifTruncate
              maxWidth="100%"
              tooltipMaxWidth="300px"
              tooltipPlacement="left"
              tooltipStrategy="fixed"
              tooltipText={contact.title}
            >
              {contact.title}
            </MotifTruncate>
          </span>
        )}
        <span className="teams-panel__link">
          <span className="material-icons-outlined" aria-hidden="true">
            mail
          </span>
          {contact.email ? (
            <MotifTruncate
              maxWidth="100%"
              tooltipMaxWidth="300px"
              tooltipPlacement="left"
              tooltipStrategy="fixed"
              tooltipText={contact.email}
            >
              <a href={`mailto:${contact.email}`}>{contact.email}</a>
            </MotifTruncate>
          ) : (
            <span className="teams-panel__na">N/A</span>
          )}
        </span>
        <span className="teams-panel__link">
          <span className="material-icons-outlined" aria-hidden="true">
            phone
          </span>
          {contact.phone ? (
            <MotifTruncate
              maxWidth="100%"
              tooltipMaxWidth="300px"
              tooltipPlacement="left"
              tooltipStrategy="fixed"
              tooltipText={contact.phone}
            >
              <a href={`tel:${contact.phone}`}>{contact.phone}</a>
            </MotifTruncate>
          ) : (
            <span className="teams-panel__na">N/A</span>
          )}
        </span>
      </div>
    </div>
  );
}

export function TeamsPanel() {
  const router = useRouter();
  const closePanel = useHeaderStore((s) => s.closePanel);
  const currentClient = useClientStore((s) => s.current);
  const [eyContacts, setEyContacts] = useState<ContactView[]>([]);
  const [clientContacts, setClientContacts] = useState<ContactView[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!currentClient?.id) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    http<{ teamMembers: TeamContactDto[] }>(Endpoints.myTeamsContacts(), {
      headers: { 'X-ClientId': currentClient.id },
    })
      .then((res) => {
        if (cancelled) return;
        const members = res.teamMembers ?? [];
        const keyContacts = members.filter((m) => m.keyContact);

        const ey = dedupeByEmail(keyContacts.filter((m) => m.type === 'Internal').map(mapContact));
        const client = dedupeByEmail(
          keyContacts
            .filter((m) => m.type === 'External' || m.type === 'ThirdParty')
            .map(mapContact),
        );

        setEyContacts(ey);
        setClientContacts(client);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[TeamsPanel] Failed to load contacts:', err);
        setError('Could not load team contacts.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [currentClient?.id]);

  const openMyTeams = () => {
    const resolution = resolveLegacyRouteTarget('myteams');
    getTracker().event('Header-teams-open', { appId: 'myteams', supported: resolution.supported });
    if (!resolution.supported) {
      pushToast({ variant: 'info', message: resolution.reason ?? 'My Teams is not available.' });
      return;
    }
    closePanel();
    if (resolution.kind === 'popup') {
      openPopupMfe(resolution.appId);
      return;
    }
    if (resolution.kind === 'external') {
      window.open(resolution.url, '_blank', 'noopener,noreferrer');
      return;
    }
    void router.navigate({ to: resolution.path });
  };

  if (isLoading) {
    return (
      <div className="header-utility-panel__status" role="status">
        <LegacySpinner />
        <span>Loading&hellip;</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="header-utility-panel__status" role="alert">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="teams-panel">
      <div className="teams-panel__section">
        <h3 className="teams-panel__section-header">EY Key Contacts</h3>
        {eyContacts.length > 0 ? (
          eyContacts.map((c) => <ContactCard key={c.email} contact={c} />)
        ) : (
          <p className="teams-panel__empty">No information available.</p>
        )}
      </div>

      <div className="teams-panel__section">
        <h3 className="teams-panel__section-header">Client Key Contacts</h3>
        {clientContacts.length > 0 ? (
          clientContacts.map((c) => <ContactCard key={c.email} contact={c} />)
        ) : (
          <p className="teams-panel__empty">You do not have any client contacts.</p>
        )}
      </div>

      <div className="teams-panel__footer">
        <MotifButton
          variant="secondary"
          type="button"
          fullWidth
          className="teams-panel__see-more"
          onClick={openMyTeams}
        >
          See More
        </MotifButton>
      </div>
    </div>
  );
}
